/**
 * @description
 * Client VSCode Lens page for client users.
 * Shows VSCode Lens functionality with domain-filtered data.
 * Identical to admin VSCodeLens.tsx with full feature parity.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-22
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { AlertCircle, Database, RefreshCw, Settings } from 'lucide-react';
import { useVSCodeLens } from '../hooks/useVSCodeLens';
import { TableSelector } from '../components/vscode-lens/TableSelector';
import { FilterPanel } from '../components/vscode-lens/FilterPanel';
import { ActiveFilters } from '../components/vscode-lens/ActiveFilters';
import { DataTable } from '../components/vscode-lens/DataTable';
import { StreamControls } from '../components/vscode-lens/StreamControls';
import { UserAnalytics } from '../components/vscode-lens/UserAnalytics';
import { TableSpecificInsights } from '../components/vscode-lens/TableSpecificInsights';
import { VSCodeLensConfigModal } from '../components/vscode-lens/VSCodeLensConfigModal';
import type { FilterConfig, VSCodeLensTable } from '../../types/vscode-lens';

/**
 * Client VSCode Lens page component
 */
export function ClientVSCodeLens() {
  const {
    tables,
    tablesLoading,
    tablesError,
    isStreaming,
    streamData,
    rawStreamData,
    streamStatus,
    streamError,
    domainAggregation,
    domainFilterConfig,
    setDomainFilterConfig,
    userAggregation,
    userStatistics,
    userFilterConfig,
    setUserFilterConfig,
    setAnalyticsViewMode,
    startStream,
    stopStream,
    clearData,
    exportData,
    refreshTables,
  } = useVSCodeLens();

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedTable, setSelectedTable] = useState<VSCodeLensTable | null>(null);
  const [filters, setFilters] = useState<FilterConfig>({
    timePreset: 'lastDay',
    batchSize: 100,
    columnFilters: {},
  });

  // Show error toast when stream error occurs
  useEffect(() => {
    if (streamError) {
      toast.error(streamError);
    }
  }, [streamError]);

  // Show success toast when stream completes
  useEffect(() => {
    if (streamStatus.status === 'complete') {
      toast.success('Stream complete! Data loaded successfully.');
    }
  }, [streamStatus.status, streamStatus.recordsStreamed]);

  const handleStartStream = async () => {
    if (!selectedTable) {
      toast.error('Please select a table first');
      return;
    }

    try {
      clearData();
      await startStream(selectedTable, filters);
    } catch (error) {
      console.error('Failed to start stream:', error);
    }
  };

  const handleTableSelect = (table: string) => {
    setSelectedTable(table as VSCodeLensTable);
    // Clear data when changing tables
    if (streamData.length > 0) {
      clearData();
    }
  };

  const handleExport = (format: 'csv' | 'json') => {
    exportData(format);
    toast.success(`Data exported as ${format.toUpperCase()} successfully`);
  };

  const handleRemoveColumnFilter = (key: string) => {
    const newColumnFilters = { ...filters.columnFilters };
    delete newColumnFilters[key];
    setFilters({
      ...filters,
      columnFilters: newColumnFilters,
    });
  };

  // Force User Analytics mode for client users
  useEffect(() => {
    setAnalyticsViewMode('user');
  }, [setAnalyticsViewMode]);

  // Loading state
  if (tablesLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading VSCode Lens tables...</p>
        </div>
      </div>
    );
  }

  // Error state - show configuration modal instead of error message
  if (tablesError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">VSCode Lens</h1>
          <p className="text-muted-foreground">
            Real-time data streaming from VSCode Lens PostgreSQL database
          </p>
        </div>

        <div className="rounded-lg border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 p-8 shadow-lg">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700">
                <AlertCircle className="h-6 w-6 text-gray-600 dark:text-gray-300" />
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                VSCodeLens Configuration Required
              </h3>
              <p className="mt-2 text-lg text-gray-700 dark:text-gray-300">
                {tablesError}
              </p>
              <p className="mt-3 text-base text-gray-600 dark:text-gray-400">
                Configure your VSCodeLens API settings to start streaming data.
              </p>
              <button
                onClick={() => setShowConfigModal(true)}
                className="mt-4 inline-flex items-center gap-3 rounded-lg bg-gray-900 px-6 py-3 text-base font-semibold text-white shadow-lg transition-all duration-200 hover:bg-gray-800 hover:shadow-xl hover:scale-105 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-gray-200"
              >
                <Settings className="h-5 w-5" />
                Configure VSCodeLens
              </button>
            </div>
          </div>
        </div>

        <VSCodeLensConfigModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          onConfigured={() => {
            setShowConfigModal(false);
            refreshTables(); // Refresh tables after configuration
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Database className="h-8 w-8" />
            VSCode Lens
          </h1>
          <p className="text-muted-foreground">
            Real-time data streaming from VSCode Lens PostgreSQL database
          </p>
        </div>
        <button
          onClick={refreshTables}
          disabled={isStreaming}
          className="flex items-center gap-2 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh Tables
        </button>
      </div>

      {/* Configuration Panel */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column - Table & Filters */}
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-lg border bg-card p-6">
            <TableSelector
              tables={tables}
              selectedTable={selectedTable}
              onSelect={handleTableSelect}
              disabled={isStreaming}
            />
          </div>

          {selectedTable && (
            <div className="rounded-lg border bg-card p-6">
              <FilterPanel
                table={selectedTable}
                filters={filters}
                onChange={setFilters}
                disabled={isStreaming}
                domainAggregation={domainAggregation}
                domainFilterConfig={domainFilterConfig}
                onDomainFilterChange={setDomainFilterConfig}
              />
            </div>
          )}
        </div>

        {/* Right Column - Status & Controls */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Filters Display */}
          {selectedTable && (
            <ActiveFilters
              filters={filters}
              onRemoveColumnFilter={handleRemoveColumnFilter}
            />
          )}

          <div className="rounded-lg border bg-card p-6">
            <StreamControls
              isStreaming={isStreaming}
              onStart={handleStartStream}
              onStop={stopStream}
              onExport={handleExport}
              disabled={!selectedTable}
              hasData={streamData.length > 0}
            />
          </div>
        </div>
      </div>



          {/* Table-Specific Insights */}
          {rawStreamData.length > 0 && selectedTable && (
            <TableSpecificInsights
              table={selectedTable}
              data={rawStreamData}
              userAggregation={userAggregation}
              userStatistics={userStatistics}
              domainAggregation={domainAggregation}
            />
          )}


          {/* User Analytics Panel */}
          {rawStreamData.length > 0 && userAggregation && userStatistics && selectedTable && (
            <UserAnalytics
              aggregation={userAggregation}
              statistics={userStatistics}
              filterConfig={userFilterConfig}
              onFilterChange={setUserFilterConfig}
              table={selectedTable}
              rawData={rawStreamData}
            />
          )}

          {/* Data Table */}
          {streamData.length > 0 && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold">
                  Data ({streamData.length.toLocaleString()} records)
                </h2>
              </div>
              <DataTable data={streamData} isLoading={isStreaming} />
            </div>
          )}

          {/* Empty State */}
          {streamData.length === 0 && !isStreaming && selectedTable && (
            <div className="rounded-lg border bg-card p-12 text-center">
              <Database className="mx-auto h-12 w-12 text-muted-foreground" />
              <h3 className="mt-4 text-lg font-semibold">No Data Yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Click "Load Data" to begin loading data from {selectedTable}
              </p>
            </div>
          )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
